import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Search, Filter, Eye, Trash2, Loader2, Download, FileDown, ChevronLeft, ChevronRight, X, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { getCountryName, getCategoryLabel } from '../lib/constants';
import { formatFactorValue } from '../lib/format';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Multi-select filter component with search
const MultiSelectFilter = ({ label, options, selected, onSelectionChange, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onSelectionChange(selected.filter(v => v !== value));
    } else {
      onSelectionChange([...selected, value]);
    }
  };

  const clearAll = () => {
    onSelectionChange([]);
  };

  const selectAll = () => {
    onSelectionChange(filteredOptions.map(opt => opt.value));
  };

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const searchLower = searchTerm.toLowerCase();
    return options.filter(opt => 
      opt.label.toLowerCase().includes(searchLower) ||
      opt.value.toLowerCase().includes(searchLower)
    );
  }, [options, searchTerm]);

  // Reset search when popover closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`h-8 border-dashed ${className}`}>
          <Filter className="mr-2 h-3 w-3" />
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
              {selected.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <div className="p-2 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{label}</span>
            <div className="flex gap-1">
              {filteredOptions.length > 0 && (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={selectAll}>
                  All
                </Button>
              )}
              {selected.length > 0 && (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={clearAll}>
                  Clear
                </Button>
              )}
            </div>
          </div>
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 pl-7 text-sm"
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-auto p-2">
          {filteredOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2 text-center">
              {options.length === 0 ? 'No options' : 'No matches found'}
            </p>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2 py-1 px-1 rounded hover:bg-gray-100 cursor-pointer"
                onClick={() => toggleOption(option.value)}
              >
                <Checkbox
                  checked={selected.includes(option.value)}
                  className="pointer-events-none"
                />
                <span className="text-sm truncate flex-1" title={option.label}>
                  {option.label}
                </span>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function EmissionFactorsPage() {
  const [factors, setFactors] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [exportingActivitySet, setExportingActivitySet] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { canEdit, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Multi-select filter states
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [activityClassificationFilter, setActivityClassificationFilter] = useState([]);
  const [subCategoryFilter, setSubCategoryFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [subTypeFilter, setSubTypeFilter] = useState([]);
  const [countryFilter, setCountryFilter] = useState([]);
  const [inputUnitFilter, setInputUnitFilter] = useState([]);

  // Distinct filter dropdown values, loaded once from the server
  const [filterOptionsData, setFilterOptionsData] = useState({
    categories: [],
    activity_classifications: [],
    sub_categories: [],
    types: [],
    sub_types: [],
    countries: [],
    input_units: [],
  });

  const fetchFactors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      categoryFilter.forEach(v => params.append('category', v));
      activityClassificationFilter.forEach(v => params.append('activity_classification', v));
      subCategoryFilter.forEach(v => params.append('sub_category', v));
      typeFilter.forEach(v => params.append('type', v));
      subTypeFilter.forEach(v => params.append('sub_type', v));
      countryFilter.forEach(v => params.append('country', v));
      inputUnitFilter.forEach(v => params.append('input_unit', v));
      params.append('page', String(currentPage));
      params.append('page_size', String(pageSize));

      const response = await axios.get(`${API_URL}/emission-factors?${params}`);
      setFactors(response.data.items || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      toast.error('Failed to load emission factors');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, categoryFilter, activityClassificationFilter, subCategoryFilter,
      typeFilter, subTypeFilter, countryFilter, inputUnitFilter, currentPage, pageSize]);

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(`${API_URL}/emission-factors/export/excel`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'emission_factors.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      toast.error('Failed to download Excel file');
    } finally {
      setDownloading(false);
    }
  };

  // Downloads prod's latest activity set table
  const handleExportActivitySet = async () => {
    setExportingActivitySet(true);
    try {
      const response = await axios.get(`${API_URL}/activity-sets/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'activity_set.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export activity set');
    } finally {
      setExportingActivitySet(false);
    }
  };

  // Blank bulk-upload template, for roles that can actually submit factors.
  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const response = await axios.get(`${API_URL}/emission-factors/bulk-template`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'emission_factors_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Debounce the search box so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch the current page whenever filters/search/pagination change
  useEffect(() => {
    fetchFactors();
  }, [fetchFactors]);

  // Load distinct filter dropdown values once on mount
  useEffect(() => {
    axios.get(`${API_URL}/emission-factors/filter-options`)
      .then(res => setFilterOptionsData(res.data))
      .catch(() => { /* dropdowns just stay empty if this fails */ });
  }, []);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/emission-factors/${deleteId}`);
      toast.success('Emission factor deleted');
      setDeleteId(null);
      fetchFactors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Build filter dropdown options from the distinct values loaded from the server
  const filterOptions = useMemo(() => {
    const o = filterOptionsData;
    return {
      categories: (o.categories || []).map(c => ({ value: c, label: getCategoryLabel(c) })),
      activityClassifications: (o.activity_classifications || []).map(a => ({ value: a, label: a })),
      subCategories: (o.sub_categories || []).map(s => ({ value: s, label: s })),
      types: (o.types || []).map(t => ({ value: t, label: t })),
      subTypes: (o.sub_types || []).map(s => ({ value: s, label: s })),
      countries: (o.countries || []).map(c => ({ value: c, label: getCountryName(c) })),
      inputUnits: (o.input_units || []).map(u => ({ value: u, label: u })),
    };
  }, [filterOptionsData]);

  // Check if any column filter is active
  const hasActiveFilters = categoryFilter.length > 0 || activityClassificationFilter.length > 0 || subCategoryFilter.length > 0 || 
    typeFilter.length > 0 || subTypeFilter.length > 0 || countryFilter.length > 0 || inputUnitFilter.length > 0;

  const clearAllFilters = () => {
    setCategoryFilter([]);
    setActivityClassificationFilter([]);
    setSubCategoryFilter([]);
    setTypeFilter([]);
    setSubTypeFilter([]);
    setCountryFilter([]);
    setInputUnitFilter([]);
  };

  // Filtering, search and pagination are all handled server-side. `factors`
  // already holds just the current page for the active filters.
  const paginatedFactors = factors;

  // Reset to page 1 when the active query changes (avoids requesting a page
  // number that no longer exists after filters narrow the result set).
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, pageSize, categoryFilter, activityClassificationFilter, subCategoryFilter, typeFilter, subTypeFilter, countryFilter, inputUnitFilter]);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="emission-factors-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Emission Factors</h1>
            <p className="text-muted-foreground mt-1">Manage all emission factors in the system</p>
          </div>
          <div className="flex gap-2">
            {canEdit() && (
              <>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={handleDownloadExcel}
                  disabled={downloading}
                  data-testid="download-excel-btn"
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Export Excel
                </Button>
                <Button variant="outline" className="gap-2" onClick={handleExportActivitySet} disabled={exportingActivitySet} data-testid="export-activity-set-btn">
                  {exportingActivitySet ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Export Activity Set
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                  data-testid="download-template-btn"
                >
                  {downloadingTemplate ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileDown className="w-4 h-4" />
                  )}
                  Download Template
                </Button>
                <Link to="/emission-factors/bulk">
                  <Button variant="outline" className="gap-2" data-testid="bulk-add-btn">
                    <Plus className="w-4 h-4" />
                    Bulk Add
                  </Button>
                </Link>
                <Link to="/emission-factors/new">
                  <Button className="btn-primary gap-2" data-testid="create-ef-btn">
                    <Plus className="w-4 h-4" />
                    New Factor
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Search and Status Filter */}
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              {/* Search and Status Row */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by EF ID, category, type, sub type, country..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 input-eco"
                    data-testid="search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48 input-eco" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Column Filters Row */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground mr-1">Filters:</span>
                <MultiSelectFilter
                  label="Category"
                  options={filterOptions.categories}
                  selected={categoryFilter}
                  onSelectionChange={setCategoryFilter}
                />
                <MultiSelectFilter
                  label="Activity Classification"
                  options={filterOptions.activityClassifications}
                  selected={activityClassificationFilter}
                  onSelectionChange={setActivityClassificationFilter}
                />
                <MultiSelectFilter
                  label="Sub Category"
                  options={filterOptions.subCategories}
                  selected={subCategoryFilter}
                  onSelectionChange={setSubCategoryFilter}
                />
                <MultiSelectFilter
                  label="Type"
                  options={filterOptions.types}
                  selected={typeFilter}
                  onSelectionChange={setTypeFilter}
                />
                <MultiSelectFilter
                  label="Sub Type"
                  options={filterOptions.subTypes}
                  selected={subTypeFilter}
                  onSelectionChange={setSubTypeFilter}
                />
                <MultiSelectFilter
                  label="Country"
                  options={filterOptions.countries}
                  selected={countryFilter}
                  onSelectionChange={setCountryFilter}
                />
                <MultiSelectFilter
                  label="Input Unit"
                  options={filterOptions.inputUnits}
                  selected={inputUnitFilter}
                  onSelectionChange={setInputUnitFilter}
                />
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" className="h-8 px-2" onClick={clearAllFilters}>
                    <X className="w-3 h-3 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : total === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No emission factors found</p>
                {canEdit() && (
                  <Link to="/emission-factors/new">
                    <Button className="mt-4 btn-primary gap-2">
                      <Plus className="w-4 h-4" />
                      Create your first factor
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="table-dense">ID</TableHead>
                        <TableHead className="table-dense">Category</TableHead>
                        <TableHead className="table-dense">Sub Category</TableHead>
                        <TableHead className="table-dense">Type</TableHead>
                        <TableHead className="table-dense">Sub Type</TableHead>
                        <TableHead className="table-dense">Input Unit</TableHead>
                        <TableHead className="table-dense">Country</TableHead>
                        <TableHead className="table-dense">CO2e</TableHead>
                        <TableHead className="table-dense">CO2 Factor</TableHead>
                        <TableHead className="table-dense">Source</TableHead>
                        <TableHead className="table-dense">Year</TableHead>
                        <TableHead className="table-dense">Status</TableHead>
                        <TableHead className="table-dense text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedFactors.map((factor) => {
                        const isSiteSpecific = !!(factor.site_identity && String(factor.site_identity).trim());
                        return (
                        <TableRow
                          key={factor.id}
                          className={`table-dense ${isSiteSpecific ? 'bg-blue-50/60 hover:bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                          data-testid={`ef-row-${factor.id}`}
                          data-site-specific={isSiteSpecific ? 'true' : 'false'}
                        >
                          <TableCell className="font-mono text-sm">
                            <div className="flex flex-col items-start gap-1">
                              <span className="inline-flex items-center gap-1">
                                {factor.emission_factor_id}
                                {factor.double_verified && (
                                  <span
                                    className="inline-flex text-emerald-500"
                                    title="Cross-checked"
                                    data-testid={`cross-checked-${factor.id}`}
                                  >
                                    <BadgeCheck className="w-3.5 h-3.5" />
                                  </span>
                                )}
                              </span>
                              {isSiteSpecific && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-300"
                                  title={`Site-specific factor (${factor.site_identity})`}
                                  data-testid={`site-badge-${factor.id}`}
                                >
                                  Site
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{getCategoryLabel(factor.category)}</TableCell>
                          <TableCell>{factor.sub_category ?? ''}</TableCell>
                          <TableCell>{factor.type ?? ''}</TableCell>
                          <TableCell>{factor.sub_type ?? ''}</TableCell>
                          <TableCell>{factor.input_unit || '—'}</TableCell>
                          <TableCell>{getCountryName(factor.country)}</TableCell>
                          <TableCell className="font-mono text-sm">{formatFactorValue(factor.co2e)}</TableCell>
                          <TableCell className="font-mono text-sm">{formatFactorValue(factor.co2factor)}</TableCell>
                          <TableCell>{factor.source_name || '—'}</TableCell>
                          <TableCell>{factor.source_version || '—'}</TableCell>
                          <TableCell>
                            <StatusBadge status={factor.status} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/emission-factors/${factor.id}`)}
                                data-testid={`view-ef-${factor.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {isAdmin() && factor.status !== 'deleted' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteId(factor.id)}
                                  className="text-destructive hover:text-destructive"
                                  data-testid={`delete-ef-${factor.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} results
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Rows per page:</span>
                        <Select value={String(pageSize)} onValueChange={(val) => setPageSize(Number(val))}>
                          <SelectTrigger className="w-[70px] h-8" data-testid="page-size-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAGE_SIZE_OPTIONS.map(size => (
                              <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              className="w-8 h-8 p-0"
                              onClick={() => setCurrentPage(pageNum)}
                              data-testid={`page-${pageNum}-btn`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        data-testid="next-page-btn"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Delete confirmation dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Emission Factor?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. The emission factor will be permanently removed from the system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
